import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import Chip from '@/shared/components/Chip/Chip'
import { debounce } from 'lodash'
import styles from './ChipsCell.module.scss'
import IconButton from '@/shared/components/IconButton/IconButton'
import { whiteShades } from '@/assets/styles/helpers/colors.styles'
import { floatingSurface } from '@/shared/styles/floatingSurface.styles'
import { ChipObject } from '@/shared/api/BaseAPI.model'

interface ChipsCellProps {
  chipPayload?: ChipObject[]
}

/**
 * The arrow is filled with the same grey as the panel's 2px border, so it
 * reads as that border continuing into a point. MUI's default fills it with
 * the tooltip background instead, which on a translucent, blurred panel comes
 * out as a bare dark wedge stuck to the corner.
 */
const arrowStyles = {
  color: whiteShades.shade1
}

const tooltipSlotProps = {
  tooltip: { sx: { ...floatingSurface } },
  arrow: { sx: arrowStyles }
}

function ChipsCell(props: ChipsCellProps) {
  const { chipPayload = [] } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(chipPayload?.length || 0)

  const measureVisibleChips = useCallback(() => {
    if (!containerRef.current) return

    const containerWidth = containerRef.current.offsetWidth
    let currentWidth = 16
    let fitCount = 0

    const dummy = document.createElement('div')
    dummy.style.position = 'absolute'
    dummy.style.visibility = 'hidden'
    dummy.style.display = 'flex'
    dummy.style.padding = '0 0.5rem'
    document.body.appendChild(dummy)

    for (let i = 0; i < chipPayload.length; i++) {
      const chip = document.createElement('div')
      chip.style.display = 'flex'
      chip.style.alignItems = 'center'
      chip.style.padding = '0 8px'
      chip.style.fontSize = '14px'
      chip.style.lineHeight = '20px'
      chip.style.marginRight = '4px'

      if (chipPayload[i].iconUrl) {
        const icon = document.createElement('img')
        icon.src = chipPayload[i].iconUrl || ''
        icon.style.width = '16px'
        icon.style.height = '16px'
        icon.style.marginRight = '4px'
        chip.appendChild(icon)
      }

      const label = document.createElement('span')
      label.innerText = chipPayload[i].label
      chip.appendChild(label)

      dummy.appendChild(chip)

      currentWidth += chip.offsetWidth + 4

      if (currentWidth < containerWidth || fitCount === 0) {
        fitCount++
      } else {
        break
      }
    }

    document.body.removeChild(dummy)
    setVisibleCount(fitCount > 0 ? fitCount : 0)
  }, [chipPayload])

  const debouncedMeasure = useCallback(debounce(measureVisibleChips, 20), [
    measureVisibleChips
  ])

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      debouncedMeasure()
    })

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      observer.disconnect()
      debouncedMeasure.cancel()
    }
  }, [debouncedMeasure])

  const visibleChips = visibleCount ? chipPayload.slice(0, visibleCount) : []
  const hiddenCount =
    chipPayload && chipPayload.length - visibleCount > 0
      ? chipPayload.length - visibleCount
      : 0

  const chipIcon = (previewFunction?: () => void, icon?: JSX.Element) => (
    <div className={styles.chipIconWrapper}>
      {previewFunction ? (
        <IconButton
          onClick={previewFunction}
          icon="las la-eye icon-medium"
          color="primary"
          noPadding
        />
      ) : (
        icon
      )}
    </div>
  )

  const renderChip = (chipElement: ChipObject) => {
    const chipNode = (
      <Chip
        key={chipElement.id}
        label={chipElement.label}
        chipImg={chipElement.iconUrl}
        chipIcon={chipIcon(chipElement.previewFunction, chipElement.icon)}
        color={chipElement.color || 'primary'}
      />
    )

    const tooltipItems = chipElement.tooltipContent ?? []
    if (!tooltipItems.length) return chipNode

    return (
      <Tooltip
        key={chipElement.id}
        title={
          <div className={styles.tooltipContent}>
            {tooltipItems.map((item, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={idx} className={`${styles.tooltipItem} body-small`}>
                {item}
              </p>
            ))}
          </div>
        }
        placement="top"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: { offset: [0, -22] }
              }
            ]
          },
          ...tooltipSlotProps
        }}
      >
        <span className={styles.tooltipWrapper}>{chipNode}</span>
      </Tooltip>
    )
  }

  const hiddenLabels = chipPayload
    .slice(visibleCount)
    .map(c => c.label)
    .join(', ')

  return (
    <div ref={containerRef} className={styles.chipsCellWrapper}>
      {visibleChips.map(renderChip)}

      {hiddenCount > 0 && (
        <Tooltip
          title={hiddenLabels}
          placement="top"
          arrow
          slotProps={tooltipSlotProps}
        >
          <div className={styles.hiddenCountWrapper}>
            <p className={`body-bold-medium ${styles.hiddenCountLabel}`}>
              {hiddenCount === chipPayload.length
                ? `+${hiddenCount} items`
                : `+${hiddenCount}`}
            </p>
          </div>
        </Tooltip>
      )}
    </div>
  )
}

export default memo(ChipsCell)
